/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { BadgeStyles, BadgeVariantOptions } from '../UNSAFE_Badge/themes/BadgeStyles.css';
import { BadgeRedwoodTheme } from '../UNSAFE_Badge/themes/redwood/BadgeTheme';
import { useTooltipOnTruncation } from '#hooks/PRIVATE_useTooltipOnTruncation';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';

export type Props = {
  /**
   * Sets the variant for the badge. Badge can be subtle or solid with different colors. The default value of this property is theme driven.
   */
  variant?:
    | 'neutral'
    | 'neutralSubtle'
    | 'danger'
    | 'dangerSubtle'
    | 'success'
    | 'successSubtle'
    | 'warning'
    | 'warningSubtle'
    | 'info'
    | 'infoSubtle';
  /**
   * Specifies the size of the badge. Consists of two options: Medium and small. The default value of this property is theme driven.
   */
  size?: 'sm' | 'md';
  /**
   * Specifies the edge of the badge. Badges can be attached to the end edge of another component. They lose their default corner rounding on right side for ltr direction or left side for rtl direction.
   */
  edge?: 'none' | 'end';
  /**
   * Specifies the string that will be rendered in the badge.
   */
  children: string;
};

export function TruncatingBadge({ variant, size, edge, children }: Props) {
  const { classes } = useComponentTheme<BadgeVariantOptions, BadgeStyles>(BadgeRedwoodTheme, {
    variant,
    size,
    edge
  });
  const { isTabbable } = useTabbableMode();
  const { tooltipOnTruncationProps, tooltipContent, isTextTruncated } =
    useTooltipOnTruncation(children);

  return (
    <>
      <span
        {...tooltipOnTruncationProps}
        tabIndex={isTextTruncated && isTabbable ? 0 : -1}
        class={classes}>
        {children}
      </span>
      {tooltipContent}
    </>
  );
}
